
import { useEffect, useMemo, useState } from 'react'

import { attributesSelectors } from '@/entities/attributes'
import { fetchOrderAttributes } from '@/entities/attributes/thunks/fetchOrderAttributes'
import { depotsSelectors } from '@/entities/depots'
import { planningShiftsSelectors, runsheetShiftsSelectors, runsheetsSelectors, scenariosSelectors } from '@/entities/schedules'
import { addShifts } from '@/entities/schedules/thunks/addShifts'
import { Button, Modal } from '@/shared/components'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { DialogComponentProps } from '@/shared/components/Modal/models/DialogStructure'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { faTruck } from '@fortawesome/pro-solid-svg-icons'
import { styled, Typography } from '@mui/material'
import { GridRowSelectionModel } from '@mui/x-data-grid-premium'

import { addRunDialogColumnDefs } from './libs/addRunDialogColumnDefs'
import { useColumnGroupingModel } from './libs/useColumnGroupingModel'
import { useFetchScheduleRuns } from './libs/useFetchScheduleRuns'

export type AddRunDialogProps = DialogComponentProps & {
  onClose: () => void
}

export const AddRunDialog: React.FC<AddRunDialogProps> = ({
  open,
  onClose,
  scheduleId,
}) => {
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([])
  const [isLoading, setIsLoading] = useState(false)

  const depots = useAppSelector(state => depotsSelectors.selectAll(state))
  const orderAttributes = useAppSelector(state => attributesSelectors.selectAll(state))
  const currentScreen = useAppSelector(state => state.userInterface.currentScreen)

  const schedule = useAppSelector(state =>
    state.userInterface.currentScreen === 'planning' ?
      scenariosSelectors.selectById(state, scheduleId) :
      runsheetsSelectors.selectById(state, scheduleId),
  )
  const existingRunIds = useAppSelector(state =>
    state.userInterface.currentScreen === 'planning' ?
      planningShiftsSelectors.selectAll(state)
        .filter(shift => shift.scenarioId === scheduleId)
        .map(shift => shift.runId) :
      runsheetShiftsSelectors.selectAll(state)
        .filter(shift => shift.runsheetId === scheduleId)
        .map(shift => shift.runId),
  )
  const { data } = useFetchScheduleRuns(scheduleId)
  const { columnGroupingModel, initialColumnGroupVisibility } = useColumnGroupingModel()
  const dispatch = useAppDispatch()

  const rows = useMemo(() => {
    if (!data || !existingRunIds) return []

    return data
      .filter(run => !existingRunIds.includes(run.id))
      .filter(run => !run.isPublished)
      .map(run => {
        return {
          id: run.id,
          isSelectable: true,
          name: {
            text: run.name,
            uiColor: run.uiColor,
            icon: faTruck,
          },
          description: run.description,
          displayRunAttributes: run.attributeIds.map(attr => orderAttributes.find(a => a.id === attr)?.name).join(', '),
          startDepot: depots.find((depot) => depot.id === run.startDepotId)?.name || '',
          finishDepot: depots.find((depot) => depot.id === run.finishDepotId)?.name || '',
          startTimeEarliest: run.workHours.workDays[0].startTimeEarliest,
          startTimeLatest: run.workHours.workDays[0].startTimeLatest,
          weightCapacity: run.weightCapacity,
          volumeCapacity: run.volumeCapacity,
          costFixed: run.costFixed,
          costPerHour: run.costPerHour,
          costPerKm: run.costPerKm,
          maxWorkingTime: run.workHours.workDays[0].maxWorkingTime,
          maxDrivingTime: run.workHours.workDays[0].maxDrivingTime,
          isActive: run.isActive,
        }
      })
  }, [data, existingRunIds, depots, orderAttributes])

  const onSubmit = async () => {
    if (!schedule || rowSelectionModel.length === 0) return
    setIsLoading(true)

    const shifts = rowSelectionModel.map((id) => ({
      runId: id,
    }))

    await dispatch(addShifts({ id: schedule.id, shifts: shifts, onSuccess: onClose }))
    setIsLoading(false)
  }

  useEffect(() => {
    dispatch(fetchOrderAttributes())
  }, [dispatch])

  return (
    <Modal
      title={`Add Runs to Scenario: ${schedule?.name}`}
      onClose={onClose}
      open={open}
      transition="opacity"
      showBackground={true}
      disableClose={isLoading}
    >
      <StyledContent>
        <Typography variant="body1">Select the runs to add them to the {currentScreen === 'planning' ? 'Scenario' : 'Runsheet'}:</Typography>
        <GridTable
          checkboxSelection
          disableVirtualization
          rows={rows}
          columns={addRunDialogColumnDefs}
          columnGroupingModel={columnGroupingModel}
          initialColumnGroupVisibility={initialColumnGroupVisibility}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel)
          }}
          sx={{
            height: 340,
          }}
        />
      </StyledContent>
      <StyledFormActions>
        {onClose && <Button text onClick={onClose}>Cancel</Button>}
        <Button primary onClick={onSubmit} disabled={rowSelectionModel.length === 0}>
          Save
        </Button>
      </StyledFormActions>
    </Modal>
  )
}

const StyledContent = styled('div')`
  ${(props) => props.theme.breakpoints.up('sm')} {
    min-width: ${(props) => props.theme.typography.pxToRem(540)};
    max-width: ${(props) => props.theme.typography.pxToRem(840)};
  }
`

const StyledFormActions = styled('div')`
  width: 100%;
  padding-bottom: ${(props) => props.theme.typography.pxToRem(12)};
  display: flex;
  justify-content: flex-end;
  gap: ${(props) => props.theme.typography.pxToRem(12)};
  padding-top: ${(props) => props.theme.typography.pxToRem(8)};
  background-color: ${(props) => props.theme.palette.background.paper};
`